import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import {
  Typography,
  Grid,
  Box,
  Skeleton,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import DetailRow, { DetailItem } from "../common-features/DetailRow";
import { fetchUserApplicationsFormData } from "../../services/api/service/userApplicationService";
import UploadDocument from "./UploadDocument";
import LockSquare from "./../../assets/images/icons/lock-square.png";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { formatDateToShortMonth } from "../../utils/dateFormatUtils";
import CustomDivider from "../../components/common/CustomDivider";
import { IconButton, UploadButton } from "../../components/ui/Button";
import UploadIcon from "./../../assets/images/icons/upload.png";
import {
  formatName,
  getCombinedApplicationSteps,
} from "../../components/Form/StepperDyanmicForm/helper";
import {
  setApplicationStatus,
  setOpenFirstTimeOnlyPopup,
  setSelectedIndex,
} from "../../services/redux/slices/tabsSlice";
import {
  setTrackingId,
  updateFormData,
  setFetchUserApplicationData,
  setPartnerStartedJourney as setPartnerStartedJourneyAction,
  setDisplayHorizontalSteps,
  setBreData as setBreDataAction,
  setSummaryApplicationEditActive,
  setApplicationId,
  setApplicantId,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { setCoApplicantDetails as setCoApplicantDetailsAction } from "../../services/redux/slices/applicationLoanDetailsSlice";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { setSalesUserId } from "../../services/redux/slices/chatSlice";
import { setPartnerDetails } from "../../services/redux/slices/applicationLoanDetailsSlice";
import TabLayout from "../../components/layouts/TabLayout";
import { ROUTES } from "../../routes/routerConfig";
import ApplicationDetails from "./ApplicationDetails";
import ConsentDetails from "./ConsentDetails";
import { ApplicantTabsHeaderActions } from "../common-features/ApplicantTabs/components/ApplicantTabsHeaderActions";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { decrypt } from "../../utils/crypto";
import { trackCustomerJourneyEvent } from "../../utils/ga";

// --- Interfaces ---
interface SummaryProps {
  // Props remain the same, but the component's internal rendering changes
  setEnableCustomerJourney: (enableCustomerJourney: boolean) => void;
  enableCustomerJourney: boolean;
  courseName: string | undefined;
  setCourseName: (courseName: string) => void;
  hideHeaderActions?: boolean;
  dataView?: string;
  callUpdateShowCustomerDataFlagApi?: (showCustomerData: boolean, applicationNumber: string) => void;
  documentsHeaderActionsPortalTarget?: HTMLDivElement | null;
}

const Summary: React.FC<SummaryProps> = ({
  setEnableCustomerJourney,
  enableCustomerJourney,
  courseName,
  setCourseName,
  hideHeaderActions = false,
  dataView,
  callUpdateShowCustomerDataFlagApi,
  documentsHeaderActionsPortalTarget: externalDocumentsHeaderActionsPortalTarget = null,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [applicationDetail, setApplicationDetail] = useState<any>(null);
  const [tabsList, setTabsList] = useState<any>(null);
  const [tabsListLoading, setTabsListLoading] = useState<any>(true);
  const [coApplicantDetails, setCoApplicantDetails] = useState<any>(null);
  const [breData, setBreData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  // const [courseName, setCourseName] = useState("");
  const [isProvisionalOfferExpanded, setIsProvisionalOfferExpanded] =
    useState(true);
  const [baseApplicationDetail, setBaseApplicationDetail] = useState<any[]>([]);
  const [mainApplicantDetails, setMainApplicantDetails] = useState<any>(null);
  const [salesPersonDetails, setSalesPersonDetails] = useState<any>(null);
  const activeLayoutTab = useSelector(
    (state: RootState) => state.tabs.selectedIndex
  );

  const [coApplicantEmploymentType, setCoApplicantEmploymentType] = useState<string>("");
  const [isPayPartyPresent, setIsPayPartyPresent] = useState<boolean>(false);
  const [isPayPartyValue, setIsPayPartyValue] = useState<string>("");

  const applicationNumber = useSelector(
    (state: RootState) => state.stepper.trackingId
  );
  const formData = useSelector((state: RootState) => state.stepper.formData);
  const applicationStatus = useSelector(
    (state: RootState) => state.tabs.applicationStatus
  );
  const fetchUserApplicationData = useSelector(
    (state: RootState) => state.stepper.fetchUserApplicationData
  );
  const actualPartnerStartedJourney = useSelector(
    (state: RootState) => state.stepper.partnerStartedJourney
  );
  const userProfile: any = useSelector(
    (state: RootState) => state.auth.userProfile
  );
  const isAuthenticated: boolean = useSelector(
    (state: RootState) => state.auth.authStatus
  );
  const normalizedApplicationStatus = String(
    fetchUserApplicationData?.status || applicationStatus || ""
  ).toLowerCase();
  const isSubmittedApplication = [
    "application submitted",
    "submitted",
  ].includes(normalizedApplicationStatus);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [partnerStartedJourney, setPartnerStartedJourney] = useState<
    boolean | null
  >(null);
  const applicationDetailRequestRef = useRef(0);
  const [documentsHeaderActionsPortalTarget, setDocumentsHeaderActionsPortalTarget] =
    useState<HTMLDivElement | null>(null);
  const effectiveDocumentsHeaderActionsPortalTarget =
    externalDocumentsHeaderActionsPortalTarget || documentsHeaderActionsPortalTarget;

  const triggerLinkApplicantData = useMemo(() => {
    if (partnerStartedJourney) {
      const result = (mainApplicantDetails || []).reduce((acc: any, item: any) => {
        if (["firstName", "lastName", "email", "phone_number"].includes(item.name)) {
          acc[item.name] = item.value;
        }
        return acc;
      }, {});

      return {
        email: result?.email || "",
        name:
          result?.firstName && result?.lastName
            ? `${result.firstName} ${result.lastName}`
            : "",
        phoneNumber: result?.phone_number || "",
      };
    }

    let email = "";
    let firstName = "";
    let lastName = "";
    let phoneNumber = "";

    fetchUserApplicationData?.horizontalSteps?.[0]?.verticalSteps?.forEach(
      (verticalStep: any) => {
        verticalStep?.fields?.forEach((field: any) => {
          if (field?.email) email = field.email;
          if (field?.firstName) firstName = field.firstName;
          if (field?.lastName) lastName = field.lastName;
          if (field?.phone_number) phoneNumber = field.phone_number;
        });
      }
    );

    return {
      email,
      name: firstName && lastName ? `${firstName} ${lastName}` : "",
      phoneNumber,
    };
  }, [fetchUserApplicationData, mainApplicantDetails, partnerStartedJourney]);

  const handleTriggerLinkToCustomer = useCallback(async () => {
    try {
      const response = await eligibilityCheckServices.triggerLinkToCollectDocuments({
        email: triggerLinkApplicantData.email,
        name: triggerLinkApplicantData.name,
        mobileNumber: triggerLinkApplicantData.phoneNumber,
        submissionTrackingId: applicationNumber,
        toUser: true,
      });

      if (response?.status === 200) {
        trackCustomerJourneyEvent(
          "ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER",
          isAuthenticated ? decrypt(userProfile?.id) ?? "" : "",
          applicationNumber,
          {
            label: "ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER",
            category: "ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER",
            action: "ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER",
            page: "Send Link to Customer",
          }
        );
        dispatch(
          setStatusPopup({
            status: "success",
            message: response?.data?.message,
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  }, [
    applicationNumber,
    dispatch,
    isAuthenticated,
    triggerLinkApplicantData,
    userProfile?.id,
  ]);
  const location = useLocation();
  const breadcrumbItems = isXsOrSm
    ? [
      {
        label: `${courseName || ""} [#${applicationNumber || ""}]`,
        link: "",
      },
      { label: "Upload Document", link: "/application/upload-document" },
    ]
    : [
      { label: "Dashboard", link: "/dashboard" },
      {
        label: `${courseName || ""} [#${applicationNumber || ""}]`,
        link: "",
      },
      { label: "Upload Document", link: "/application/upload-document" },
    ];

  /**
   * Groups co-applicant fields by applicant number from a flat array of field objects
   *
   * This function processes an array of field objects where each object contains
   * co-applicant data with keys in the format "coApplicant{number}{fieldName}".
   * It extracts the applicant number and field name, then groups all fields
   * belonging to the same co-applicant together.
   *
   * @param {any[]} fieldsList - Array of field objects containing co-applicant data
   * @returns {Record<string, any>} Grouped object where keys are applicant numbers and values are objects containing all fields for that applicant
   *
   * @example
   * Input: [
   *   { "coApplicant1Name": "John" },
   *   { "coApplicant1Email": "john@example.com" },
   *   { "coApplicant2Name": "Jane" }
   * ]
   * Output: {
   *   "1": { "Name": "John", "Email": "john@example.com" },
   *   "2": { "Name": "Jane" }
   * }
   */
  const getCoApplicationDetail = (fieldsList: any) => {
    const grouped: Record<string, any> = {};
    fieldsList?.map((item: any) => {
      const [key, value] = Object.entries(item)[0];
      const match = key.match(/basicCoApplicant(\d+)(.+)/);
      if (match) {
        const [, groupNumber, field] = match;
        if (!grouped[groupNumber]) grouped[groupNumber] = {};
        grouped[groupNumber][field] = value;
      }
      return null; // map needs to return something
    });

    return grouped;
  };

  const getVerticalStepFieldsByIds = (
    applicationData: any,
    horizontalStepId: number,
    verticalStepId: number
  ) =>
    applicationData?.horizontalSteps
      ?.find((step: any) => Number(step?.horizontalStepId) === horizontalStepId)
      ?.verticalSteps?.find(
        (step: any) => Number(step?.verticalStepId) === verticalStepId
      )?.fields || [];

  const mergeUniqueFields = (...fieldLists: any[][]) => {
    const fieldMap = new Map<string, any>();
    fieldLists.flat().forEach((field: any) => {
      const key = Object.keys(field || {})[0];
      if (key) fieldMap.set(key.toLowerCase(), field);
    });
    return Array.from(fieldMap.values());
  };

  /**
   * Fetches application details and co-applicant information from the API
   *
   * This function retrieves comprehensive application data including:
   * - Basic application details (country, university, institute, course type, loan amount)
   * - Co-applicant information and generates tab names for each co-applicant
   * - Processes and structures the raw API response data for component consumption
   *
   * @async
   * @function handleFetchApplicationDetail
   * @returns {Promise<void>} Updates component state with fetched data
   */
  const handleFetchPartnerLeadDetailsApi = async (
    currentLeadId: string,
    currentTrackingId: string,
    currentBaseDetails: any[],
    isLatestRequest: () => boolean = () => true
  ) => {
    try {
      const payload = {
        leadId: currentLeadId,
        trackingId: currentTrackingId,
      };
      const res: any = await eligibilityCheckServices.partnerGetLeadDetailsApi(
        payload
      );
      if (!isLatestRequest()) return;
      const partnerDetails = res.data;

      if (partnerDetails) {
        dispatch(setPartnerDetails(partnerDetails?.data || {}));
        const appDetails: DetailItem[] =
          partnerDetails?.data?.applicationDetails || [];

        const getValue = (list: DetailItem[], key: string): string => {
          const item = list.find((item: any) => item.name === key);
          const value = item?.value;
          return value != null ? String(value) : "";
        };

        setSalesPersonDetails({
          name: getValue(appDetails, "salesManagerName"),
          email: getValue(appDetails, "salesManagerEmail"),
          mobile: getValue(appDetails, "salesManagerMobile"),
          createdOn: getValue(appDetails, "createdOn"),
        });
        setCourseName(getValue(appDetails, "courseName"));
        dispatch(setApplicationStatus(getValue(appDetails, "status")));

        const newPartnerData = [
          {
            label: "Partner Name",
            value: getValue(appDetails, "partnerName") || "-",
          },
          {
            label: "Sales Manager Name",
            value: getValue(appDetails, "salesManagerName") || "-",
          }
        ];

        const filteredApplicationDetails = (
          partnerDetails?.data?.applicationDetails || []
        ).filter((item: any) => {
          const name = (item.name || "").toLowerCase();
          return (
            name !== "applicationid" &&
            name !== "salesmanagername" &&
            name !== "salesmanageremail" &&
            name !== "salesmanagermobile" &&
            name !== "coursename" &&
            name !== "status" &&
            name !== "createdon"
          );
        });
        const finalDataJson: any = [
          ...filteredApplicationDetails,
          ...newPartnerData,
        ];

        setApplicationDetail([...finalDataJson, {
          label: "Created On",
          value: formatDateToShortMonth(getValue(appDetails, "createdOn")),
        }]);

        if (partnerDetails?.data?.applicantDetails) {
          setMainApplicantDetails(partnerDetails.data.applicantDetails);
        }

        if (partnerDetails?.data?.coApplicantDetails) {
          const partnerCoApplicantDetails =
            partnerDetails.data.coApplicantDetails;

          const getPartnerCoApplicationDetail = (detailsArr: any[]) => {
            const grouped: Record<string, any> = {};
            detailsArr.forEach((obj: any) => {
              const key = Object.keys(obj)[0];
              const arr = obj[key];
              grouped[key] = {};
              arr.forEach((fieldObj: any) => {
                grouped[key][fieldObj.name] = fieldObj.value;
              });
            });

            return grouped;
          };

          dispatch(setCoApplicantDetailsAction(getPartnerCoApplicationDetail(partnerCoApplicantDetails)));

          setCoApplicantDetails(
            getPartnerCoApplicationDetail(partnerCoApplicantDetails)
          );

          const coApplicantDetailsArr = partnerDetails.data.coApplicantDetails;
          const coApplicantTabNameList = [];
          for (let i = 0; i < coApplicantDetailsArr.length; i++) {
            const key = Object.keys(coApplicantDetailsArr[i])[0];
            const numberMatch = key.match(/coapplicant(\d+)/i);
            const number = numberMatch ? parseInt(numberMatch[1]) : i + 1;
            coApplicantTabNameList.push({
              number,
              name: `Co-Applicant ${number}`,
            });
          }
          coApplicantTabNameList.sort((a, b) => a.number - b.number);
          const sortedTabNames = coApplicantTabNameList.map(
            (item) => item.name
          );
          sortedTabNames.unshift("Applicant");
          setTabsList(sortedTabNames);
        } else {
          setTabsList(["Applicant"]);
        }
      }
    } catch {
      if (!isLatestRequest()) return;
      // Fallback if partner API fails
      setTabsList(["Applicant"]);
    } finally {
      if (!isLatestRequest()) return;
      // Stop tabs loading here for Partner flow
      setTabsListLoading(false);
    }
  };

  const handleFetchApplicationDetail = async () => {
    const requestId = applicationDetailRequestRef.current + 1;
    applicationDetailRequestRef.current = requestId;
    const isLatestRequest = () => requestId === applicationDetailRequestRef.current;
    setIsLoading(true);
    setTabsListLoading(true);
    try {
      // Get authentication token from environment variables
      const token: any = process.env.REACT_APP_FETCH_TOKEN;

      // Fetch application form data using the application number
      const res: any = await fetchUserApplicationsFormData(
        token || "",
        applicationNumber
      );
      if (!isLatestRequest()) return;

      if (res.status === 200) {
        const applicationData = res?.data?.applications?.[0];

        // Capture these values locally for immediate use
        const currentLeadId = applicationData?.leadId || "";
        const isPartnerJourney = applicationData?.partnerStartedJourney;
        const shouldUsePartnerDataView = dataView
          ? dataView === "processio_data_view"
          : true;
        const effectivePartnerStartedJourney = Boolean(
          isPartnerJourney && shouldUsePartnerDataView
        );
        const effectiveApplicationData =
          isPartnerJourney === effectivePartnerStartedJourney
            ? applicationData
            : {
                ...applicationData,
                partnerStartedJourney: effectivePartnerStartedJourney,
              };
        const currentSubmissionId = applicationData?.submissionId || "";

        setSalesPersonDetails({
          name: applicationData?.assignedSMName,
          email: applicationData?.assignedSMEmail,
          mobile: applicationData?.assignedSMMobileNumber,
          createdOn: applicationData?.createdAt,
        });

        dispatch(setApplicationId(applicationData?.applicationId));
        dispatch(setApplicantId(applicationData?.applicantId));

        setLeadId(currentLeadId);
        setPartnerStartedJourney(effectivePartnerStartedJourney);
        dispatch(setPartnerStartedJourneyAction(isPartnerJourney));
        setEnableCustomerJourney(applicationData?.assistedJourneyRequired);

        if (!effectivePartnerStartedJourney) {
          const courseNameValue: string = applicationData?.horizontalSteps?.[0]?.verticalSteps.find((elem: any) => elem.verticalStepName === "Provide Course Details")?.fields?.filter((item: any) => item.courseName) || "";
          const courceValue: any = courseNameValue?.[courseNameValue.length - 1];
          setCourseName(courceValue?.courseName || "");
        }

        const apps = res.data?.applications || [];

        // this logic is for getting customized array of horizontalsteps
        let finalSteps: any[] = [];
        for (let i = 0; i < apps.length; i++) {
          const element = apps[i];
          finalSteps = getCombinedApplicationSteps(element, applicationStatus);
        }
        dispatch(setDisplayHorizontalSteps(finalSteps));

        dispatch(setTrackingId(currentSubmissionId));
        const lengthOfHorizontalSteps =
          applicationData?.horizontalSteps?.length;
        let apiFormData: any = {};
        for (let i = 0; i < lengthOfHorizontalSteps; i++) {
          const verticalSteps =
            applicationData?.horizontalSteps?.[i]?.verticalSteps;
          for (let j = 0; j < verticalSteps?.length; j++) {
            const fields = verticalSteps?.[j]?.fields;
            for (let k = 0; k < fields?.length; k++) {
              const field = fields?.[k];
              apiFormData = { ...apiFormData, ...field };
            }
          }
        }
        dispatch(updateFormData(apiFormData));

        let loanAmount =
          applicationData?.horizontalSteps?.[0]?.verticalSteps?.[2]?.fields?.find(
            (elem: any) => elem?.loanAmount
          )?.loanAmount;
        const formattedLoanAmount = parseFloat(loanAmount);

        const country = applicationData?.horizontalSteps?.[0]?.verticalSteps?.find((elem: any) => elem.verticalStepName === "Provide Course Details")?.fields.filter((item: any) => item.country);
        const university = applicationData?.horizontalSteps?.[0]?.verticalSteps?.find((elem: any) => elem.verticalStepName === "Provide Course Details")?.fields.filter((item: any) => item.university);
        const institute = applicationData?.horizontalSteps?.[0]?.verticalSteps?.find((elem: any) => elem.verticalStepName === "Provide Course Details")?.fields.filter((item: any) => item.institute);
        const courseType = applicationData?.horizontalSteps?.[0]?.verticalSteps?.find((elem: any) => elem.verticalStepName === "Provide Course Details")?.fields.filter((item: any) => item.courseType);

        // Define initial dataJson structure separately
        const initialDataJson: any = [
          {
            label: "Country",
            value:
              country?.length ? country[country.length - 1]?.country : "-",
          },
          {
            label: "University",
            value:
              university?.length ? university[university.length - 1]?.university : "-",
          },
          {
            label: "Institute",
            value:
              institute?.length ? institute[institute.length - 1]?.institute : "-",
          },
          {
            label: "Course Type",
            value:
              courseType?.length
                ? (String(courseType[courseType.length - 1]?.courseType).trim().toUpperCase() === "NON_STEM"
                  ? "NON-STEM"
                  : courseType[courseType.length - 1]?.courseType)
                : "-",
          },
          {
            label: "Applied Loan Amount",
            value: !isNaN(formattedLoanAmount)
              ? `₹ ${formattedLoanAmount.toLocaleString()}`
              : "-",
          },
          {
            label: "Created On",
            value: formatDateToShortMonth(applicationData.createdAt),
          },
          // {
          //   label: "Last Action Date",
          //   value: formatDateToShortMonth(
          //     res?.data?.applications?.[0].updatedAt
          //   ),
          // },
          // { label: "Completion Rate", value: "" }, // TODO: Add actual completion rate from API
        ];

        // Add Sales Manager Name ONLY IF partnerStartedJourney is false
        if (effectivePartnerStartedJourney === false) {
          initialDataJson.push(
            {
              label: "Partner Name",
              value: formatName(applicationData?.partnerName),
            },
            {
              label: "Sales Manager Name",
              value: applicationData?.assignedSMName,
            }
          );
        }

        setBaseApplicationDetail(initialDataJson);
        setApplicationDetail(initialDataJson);

        dispatch(setFetchUserApplicationData(effectiveApplicationData));
        if (!effectivePartnerStartedJourney) {
          dispatch(setApplicationStatus(res?.data?.applications?.[0]?.status));
        }
        dispatch(setSalesUserId(applicationData?.assignedSMId));

        dispatch(
          setOpenFirstTimeOnlyPopup({
            isSanctionedPopupViewed: applicationData?.SanctionedPopUp,
            isDisbursedPopupViewed: applicationData?.DisbursementPopUp,
            isConnectWithSalesPopupViewed:
              applicationData?.ConnectWithSalesPopUp,
          })
        );

        // --- BRANCHING LOGIC ---
        if (effectivePartnerStartedJourney && currentLeadId && !hideHeaderActions) {
          if(callUpdateShowCustomerDataFlagApi){
            callUpdateShowCustomerDataFlagApi(false, applicationNumber);
          }
          // If Partner Journey, wait for the second API call before finishing
          await handleFetchPartnerLeadDetailsApi(
            currentLeadId,
            currentSubmissionId || applicationNumber,
            initialDataJson,
            isLatestRequest
          );
          if (!isLatestRequest()) return;
        } else {
          // Standard Journey Logic
          const fieldsList: any = mergeUniqueFields(
            getVerticalStepFieldsByIds(applicationData, 2, 9),
            getVerticalStepFieldsByIds(applicationData, 4, 11)
          );
          const coApplicantNamesList = fieldsList?.filter(
            (obj: any) =>
              Object.keys(obj)[0].toLowerCase().includes("basiccoapplicant") &&
              Object.keys(obj)[0].toLowerCase().includes("firstname")
          );

          const coApplicantTabNameList: any = [];
          for (let i = 0; i < coApplicantNamesList?.length; i++) {
            const item = coApplicantNamesList[i];
            const number =
              Object.keys(item)?.[0]
                ?.split("basicCoApplicant")
                .join("")
                ?.split("FirstName")
                .join("") || "";
            coApplicantTabNameList.push({
              number: parseInt(number) || 0,
              name: `Co-Applicant ${number}`,
            });
          }
          coApplicantTabNameList.sort((a: any, b: any) => a.number - b.number);
          const sortedTabNames = coApplicantTabNameList?.map(
            (item: any) => item.name
          );
          sortedTabNames?.unshift("Applicant");
          setTabsList(sortedTabNames);

          // dispatch(setCoApplicantDetailsAction(getCoApplicationDetail(fieldsList)));
          setCoApplicantDetails(getCoApplicationDetail(fieldsList));

          // Stop tabs loading here for Standard flow
          setTabsListLoading(false);
        }

        dispatch(setFetchUserApplicationData(effectiveApplicationData));

        // setting status which will be used for display validation of summary tabs and status in horizontalsteps, which is application specific
        if (!effectivePartnerStartedJourney) {
          dispatch(setApplicationStatus(res?.data?.applications?.[0]?.status));
        }
        // setting assigned sales person id, which is application specific, will be used for display validation of Connect button and Chat
        dispatch(setSalesUserId(applicationData?.assignedSMId));

        // setting firsttime view only popup flags which will get displayed in summary page
        dispatch(
          setOpenFirstTimeOnlyPopup({
            isSanctionedPopupViewed: applicationData?.SanctionedPopUp,
            isDisbursedPopupViewed: applicationData?.DisbursementPopUp,
            isConnectWithSalesPopupViewed:
              applicationData?.ConnectWithSalesPopUp,
          })
        );

      }
    } catch {
      if (isLatestRequest()) {
        setTabsListLoading(false);
      }
    } finally {
      if (isLatestRequest()) {
        setIsLoading(false);
      }
    }
  };


  const SalespersonTooltipContent = (
    <>
      <Typography variant="caption" display="block">
        Email: {salesPersonDetails?.email || "-"}
      </Typography>
      <Typography variant="caption" display="block">
        Mobile: {salesPersonDetails?.mobile || "-"}
      </Typography>
    </>
  );

  const handleFetchBreapi = async () => {
    try {
      const payload = {
        applicationId: applicationNumber,
        name: "getProvisionalOffer",
      };
      const res: any = await eligibilityCheckServices.fetchBreApi(payload);
      if (res?.status == 200) {
        dispatch(setBreDataAction(res?.data?.data));
        setBreData(res?.data?.data);
      }
    } catch {
      setBreData(null);
    }
  };

  useEffect(() => {
    handleFetchApplicationDetail();
    if(activeLayoutTab === 1) dispatch(setSelectedIndex(activeLayoutTab));
    if (!hideHeaderActions) {
      handleFetchBreapi();
    }
  }, [applicationNumber, dataView]);

   useEffect(() => {
    if(activeLayoutTab === 1) handleFetchApplicationDetail()
  }, [activeLayoutTab]);

  const handleUploadDocument = async () => {
    try {
      const token: any = process.env.REACT_APP_FETCH_TOKEN;
      const res: any = await fetchUserApplicationsFormData(
        token,
        applicationNumber
      );

      const apps = res.data?.applications || [];
      let apiFormData: any = {};
      for (let i = 0; i < apps?.[0]?.horizontalSteps?.length; i++) {
        const verticalSteps = apps?.[0]?.horizontalSteps?.[i]?.verticalSteps;
        for (let j = 0; j < verticalSteps?.length; j++) {
          const fields = verticalSteps?.[j]?.fields;
          for (let k = 0; k < fields?.length; k++) {
            const field = fields?.[k];
            apiFormData = { ...apiFormData, ...field };
          }
        }
      }

      dispatch(updateFormData(apiFormData));
      dispatch(setTrackingId(applicationNumber));
      navigate("/application/upload-document");
    } catch (error) {
      console.error("Failed to upload document flow initialization", error);
    }
  };

  let activeStep = 5;

  const getRepaymentLabel = (type: string) => {
    let val = typeof type === "string" ? type?.toUpperCase() : type;
    switch (val) {
      case "PINT":
        return "Partial interest";
      case "SINT":
        return "Simple interest";
      case "EMI":
        return "EMI";
      default:
        return type || "-";
    }
  };

  const formatCurrency = (value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "-";
    return `₹ ${new Intl.NumberFormat('en-IN').format(num)}`;
  };


  const handleViewTabSection = useMemo(() => {
    if (activeLayoutTab === 0) {
      return (
        <>
          {/* <UploadDocument
            isLoading={isLoading}
            tabsList={tabsList}
            tabsListLoading={tabsListLoading}
            applicationDetail={applicationDetail}
            mainApplicantDetails={mainApplicantDetails}
            coApplicantDetails={coApplicantDetails}
            handleFetchApplicationDetail={handleFetchApplicationDetail}
            partnerStartedJourney={partnerStartedJourney}
            setPartnerStartedJourney={setPartnerStartedJourney}
            enableCustomerJourney={enableCustomerJourney}
            hideHeaderActions={hideHeaderActions}
            headerActionsPortalTarget={effectiveDocumentsHeaderActionsPortalTarget}
            headerActionsOnly={true}
          /> */}
          <ApplicationDetails dataView={dataView} />
        </>
      );
    }
    if (activeLayoutTab === 1) {
      return (<>
        <UploadDocument
          isLoading={isLoading}
          tabsList={tabsList}
          tabsListLoading={tabsListLoading}
          applicationDetail={applicationDetail}
          mainApplicantDetails={mainApplicantDetails}
          coApplicantDetails={coApplicantDetails}
          handleFetchApplicationDetail={handleFetchApplicationDetail}
          partnerStartedJourney={partnerStartedJourney}
          setPartnerStartedJourney={setPartnerStartedJourney}
          enableCustomerJourney={enableCustomerJourney}
          hideHeaderActions={hideHeaderActions}
          headerActionsPortalTarget={effectiveDocumentsHeaderActionsPortalTarget}
          disableHeaderActionsPortal
        />
      </>);
    }
    if (activeLayoutTab === 2) {
      return (<>
        <ConsentDetails
          formData={formData}
          mainApplicantDetails={mainApplicantDetails}
          coApplicantDetails={coApplicantDetails}
          trackingId={applicationNumber}
          onContinue={() => dispatch(setSelectedIndex(0))}
          />
      </>);
    }
  }, [activeLayoutTab,
    isLoading,
    tabsList,
    tabsListLoading,
    applicationDetail,
    mainApplicantDetails,
    coApplicantDetails,
    partnerStartedJourney,
    setPartnerStartedJourney,
    enableCustomerJourney,
    hideHeaderActions,
    handleFetchApplicationDetail,
    effectiveDocumentsHeaderActionsPortalTarget,
    dataView,
  ])

  const summaryHeaderTriggerAction =
    !hideHeaderActions &&
    !actualPartnerStartedJourney &&
    !partnerStartedJourney &&
    !isSubmittedApplication &&
    effectiveDocumentsHeaderActionsPortalTarget
      ? createPortal(
          isLoading || tabsListLoading ? (
            <Skeleton
              variant="rounded"
              width={190}
              height={39}
              sx={{ borderRadius: "6px" }}
            />
          ) : (
            <ApplicantTabsHeaderActions
              isXsOrSm={isXsOrSm}
              tabIndex={0}
              countBulkUpload={0}
              isAddingCoApplicant={false}
              enableCustomerJourney={enableCustomerJourney}
              partnerStartedJourney={Boolean(partnerStartedJourney)}
              documentsReadOnly={isSubmittedApplication}
              onAddCoApplicant={() => {}}
              onTriggerLinkToCustomer={handleTriggerLinkToCustomer}
              onViewBulkDocuments={() => {}}
              onViewCustomerUploadDocuments={() => {}}
              onOpenBulkUploadModal={() => {}}
              showTitle={false}
              triggerOnly
              primaryTriggerLink
            />
          ),
          effectiveDocumentsHeaderActionsPortalTarget
        )
      : null;

  return (
    <>
      {summaryHeaderTriggerAction}
      {location.pathname == "/application/upload-document" && (
        <Box sx={{ width: "100%", padding: "0px 0px 18px 0px" }}>
          <Breadcrumbs items={breadcrumbItems} />
        </Box>
      )}

      {location.pathname == "/assisted-portal" && (
        <Box>
          {isLoading ? (
            <Box
              sx={{
                width: "100%",
                margin: "0 auto",
                textAlign: "center",
              }}
            >
              <div className="card-contaner">
                {/* Application Details */}
                <Grid container spacing={3} sx={{ my: 2 }}>
                  <Grid item xs={12} md={8}>
                    <Grid container rowSpacing={2} columnSpacing={4}>
                      {Array.from({ length: 8 }).map((_, index) => (
                        <Grid
                          item
                          xs={6}
                          sm={3}
                          key={index}
                          sx={{ textAlign: "left" }}
                        >
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "1rem" }}
                            width="80%"
                          />
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "1rem" }}
                            width="60%"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <div className="right-side-box">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width={24}
                          height={24}
                          sx={{ mr: 1, borderRadius: "5px" }}
                        />
                        <Skeleton
                          variant="text"
                          sx={{ fontSize: "1.1rem" }}
                          width="80%"
                        />
                      </Box>
                      <Grid container spacing={2} sx={{ textAlign: "left" }}>
                        {Array.from({ length: 4 }).map((_, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "0.9rem" }}
                              width="70%"
                            />
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                              width="50%"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Box>
          ) : (
            <div className="card-contaner">
              {isXsOrSm ? (
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={2}
                    mb={1}
                    sx={{
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 1,
                        width: "100%",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TruncatedTextWithTooltip
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#333",
                          }}
                          text={courseName || "-"}
                        // limit={20}
                        />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          [#{applicationNumber}]
                        </Typography>
                      </Box>
                      <Chip
                        label={activeStep === 6 ? "Completed" : "In Progress"}
                        className="card-contaner-status"
                      />
                    </Box>
                  </Box>

                  <Grid container spacing={3} className="card-contaner-section">
                    {/* Increased spacing between details and offer box */}
                    <Grid item xs={12} md={8}>
                      <Grid container rowSpacing={2} columnSpacing={4}>
                        {/* Adjusted spacing for better alignment */}
                        {applicationDetail?.map((item: any, index: any) => (
                          <Grid item xs={6} sm={3} key={index}>
                            <Typography className="card-contaner-lable">
                              {item.label
                                ?.replace(/[_-]+/g, " ")
                                .replace(/([A-Z])/g, " $1")
                                .replace(/\s+/g, " ")
                                .replace(/^./, (str: any) => str.toUpperCase())
                                .split(" ")
                                .map(
                                  (word: any) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")
                                .trim()}
                            </Typography>
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Typography style={{ fontSize: '13px', color: '#6F6B7D' }}>{item.name === 'appliedLoanAmount' ? '₹ ' : ''}</Typography>
                              <TruncatedTextWithTooltip
                                className="card-contaner-value"
                                text={item.value}
                              // limit={15}
                              />
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <div className="right-side-box">
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          onClick={() =>
                            setIsProvisionalOfferExpanded(
                              !isProvisionalOfferExpanded
                            )
                          }
                        >
                          <img src={LockSquare} alt="lock-square" />
                          {/* Icon */}
                          <Typography
                            // className="right-side-heading"
                            sx={{
                              ml: 1.5,
                              mr: 1,
                              mb: 1,
                              fontSize: "15px",
                              fontWeight: 600,
                              color: "#333",
                            }}
                          >
                            Complete Applicant Details To Unlock Provisional
                            Offer
                          </Typography>
                          <Box sx={{ cursor: "pointer", pt: 1 }}>
                            {isProvisionalOfferExpanded ? (
                              <KeyboardArrowUpIcon
                                sx={{ color: "rgba(0, 179, 181, 1)" }}
                              />
                            ) : (
                              <KeyboardArrowDownIcon
                                sx={{ color: "rgba(0, 179, 181, 1)" }}
                              />
                            )}
                          </Box>
                        </Box>
                        {isProvisionalOfferExpanded && (
                          <Grid container spacing={2} mt={1}>
                            <Grid item xs={6}>
                              <Typography className="right-side-lable">
                                Offer loan amount:
                              </Typography>
                              <Typography className="right-side-value">
                                {formatCurrency(breData?.Max_Loan_Amount) || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className="right-side-lable">
                                Tenure:
                              </Typography>
                              <Typography className="right-side-value">
                                {breData?.Max_Tenure || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className="right-side-lable">
                                Repayment type:
                              </Typography>
                              <Typography className="right-side-value">
                                {getRepaymentLabel(breData?.repayment_type) || "-"}
                              </Typography>
                            </Grid>
                            {/* <Grid item xs={6}>
                              <Typography className="right-side-lable">
                                ROI:
                              </Typography>
                              <Typography className="right-side-value">
                                {breData?.roi || "-"}
                              </Typography>
                            </Grid> */}
                          </Grid>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Grid container spacing={3} className="card-contaner-section">
                  {/* Increased spacing between details and offer box */}
                  <Grid item xs={12} md={12}>
                    <Typography className="card-section-hedging">
                      Application Details
                    </Typography>

                    <Grid container rowSpacing={2} columnSpacing={4}>
                      <Grid item xs={12}>
                        <Grid container rowSpacing={2} columnSpacing={4}>
                          {applicationDetail == null ||
                            applicationDetail?.length === 0
                            ? Array.from({ length: 8 }).map((_, index) => (
                              <Grid item xs={12} sm={6} md={3} key={index}>
                                <Skeleton
                                  variant="text"
                                  sx={{ fontSize: "1rem" }}
                                  width="80%"
                                />
                                <Skeleton
                                  variant="text"
                                  sx={{ fontSize: "1rem" }}
                                  width="60%"
                                />
                              </Grid>
                            ))
                            : applicationDetail
                              ?.filter((item: any) => {
                                const skipFields = [
                                  "status",
                                  "applicationid",
                                  "salesmanageremail",
                                  "salesmanagermobile",
                                  "coursename",
                                  "coursecategory",
                                  "admissionstatus",
                                  "product",
                                  // "appliedloanamount",
                                  "totalcost",
                                ].map((i) => i.toLowerCase());
                                return !skipFields.includes(
                                  item.name?.toLowerCase(),
                                );
                              })
                              .map((item: any, index: any) => {
                                // Format createdOn date if present
                                const formattedItem =
                                  item.name === "createdOn" && item.value
                                    ? {
                                      ...item,
                                      value: formatDateToShortMonth(
                                        item.value,
                                      ),
                                    }
                                    : item;
                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                    key={item.label}
                                  >
                                    <DetailRow
                                      item={formattedItem}
                                      showInfoIcon={
                                        item.label === "Sales Manager Name"
                                          ? true
                                          : false
                                      }
                                      salespersonTooltipContent={
                                        SalespersonTooltipContent
                                      }
                                    />
                                  </Grid>
                                );
                              })}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </div>
          )}
        </Box>
      )}

      {(activeLayoutTab === 0 || activeLayoutTab === 1) &&
        !externalDocumentsHeaderActionsPortalTarget && (
        <Box mt={1} mb={1}>
          <Box ref={setDocumentsHeaderActionsPortalTarget} />
        </Box>
      )}

      <Box mt={1} mb={1}>
        <TabLayout
          tabs={[
            "Application Details",
            "Documents Upload",
            "Consent & Verification",
          ]}
          activeIndex={activeLayoutTab}
          onTabChange={(index) => {
            dispatch(setSelectedIndex(index));
            dispatch(setSummaryApplicationEditActive(false));
          }}
        />
      </Box>

      <Box mt={2}>
        {handleViewTabSection}
      </Box>

      {!isXsOrSm && location.pathname == "/application/apply-now" && (
        <Typography className="section-heading">Upload Doucments</Typography>
      )}

    </>
  );
};

export default Summary;
